import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Clapperboard, Users, Package, Radio, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HostingExplorer } from "@/components/stage/HostingExplorer";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { getHighlights } from "@/lib/stage";
import { StagePostCard } from "@/components/stage/StagePostCard";
import { StagePostViewer } from "@/components/stage/StagePostViewer";
import { LiveBriefingPlayer, useBriefingMeta } from "@/components/LiveBriefing";

export default function Stages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [stores, setStores] = useState(null);
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/stage/stores?limit=40").then(({ data }) => setStores(data.stores)).catch(() => setStores([]));
    getHighlights(12).then(setPosts).catch(() => {});
  }, []);
  const live = (stores || []).filter((s) => s.live);
  const briefing = useBriefingMeta("full");
  const cta = user?.role === "seller" || user?.role === "admin" ? ["/seller/stage", "Open my Stage"] : user ? ["/select-role", "Open your Stage"] : ["/register", "Open your Stage — free"];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="stages-page">
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8 items-end">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#FF9F0A] font-bold"><Clapperboard className="h-3.5 w-3.5" /> VISMARKS Stages</div>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95]">Every store<br />is a theatre.</h1>
            <p className="mt-4 text-base text-neutral-400 max-w-xl">Live shows, music, exhibitions and audience games — made by the people who sell here. Take a seat, play along, then shop what you just watched.</p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-[#0F0F10] p-5">
            <div className="font-bold">Host your own</div>
            <p className="mt-1 text-sm text-neutral-400">Pick one Hosting free — Games, Sports, Theatre, Events, Gallery, Showroom, Workshop or Jokes &amp; Riddles. Everything inside it is free, and so is your first live session.</p>
            <Button asChild className="mt-4 rounded-full bg-[#FF3B30] text-white hover:bg-[#e0352b] font-bold" data-testid="stages-cta"><Link to={cta[0]}>{cta[1]} <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
          </div>
        </div>

        {briefing && (
          <section className="mt-12 rounded-3xl border border-[#FF3B30]/40 bg-[#120B0B] p-4 md:p-6" data-testid="stages-official-broadcast">
            <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#FF3B30] font-bold"><Radio className="h-3.5 w-3.5 animate-pulse" /> Pinned · VISMARKS official broadcast</div>
            <div className="mt-3 grid lg:grid-cols-[1.6fr_1fr] gap-6 items-center">
              <LiveBriefingPlayer cut="full" testId="stages-briefing-player" />
              <div>
                <h2 className="text-base md:text-lg font-black">The VISMARKS global briefing</h2>
                <p className="mt-2 text-sm text-neutral-400">{briefing.presenter || "Our presenter"} on the speed of discovering customers worldwide, Visibility Intelligence, the Stage — and why once listed is always listed. Start with 5 free listings.</p>
                <Link to="/briefing" data-testid="stages-briefing-link" className="mt-4 inline-flex items-center gap-1 text-sm text-[#007AFF] hover:underline">Full talk &amp; transcript <ArrowRight className="h-3.5 w-3.5" /></Link>
              </div>
            </div>
          </section>
        )}

        <section className="mt-12" data-testid="stages-hostings">
          <HostingExplorer title="Choose your kind of Stage" />
        </section>

        {live.length > 0 && (
          <section className="mt-12" data-testid="stages-live">
            <h2 className="text-base md:text-lg font-bold flex items-center gap-2"><Radio className="h-4 w-4 text-[#FF3B30] animate-pulse" /> Live right now</h2>
            <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{live.map((s) => <StoreTile key={s.seller_id} s={s} />)}</div>
          </section>
        )}

        <section className="mt-12">
          <h2 className="text-base md:text-lg font-bold">Latest on Stage</h2>
          {posts.length ? (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="stages-latest">{posts.map((p) => <StagePostCard key={p.id} post={p} onOpen={() => setParams({ post: p.id })} />)}</div>
          ) : (
            <div className="mt-3 rounded-2xl border border-dashed border-white/15 p-10 text-center text-sm text-neutral-500" data-testid="stages-empty">The curtain hasn't lifted yet — be the first store to post on your Stage.</div>
          )}
        </section>

        <section className="mt-12">
          <h2 className="text-base md:text-lg font-bold">Stores with a Stage</h2>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="stages-stores">
            {stores === null && <div className="text-sm text-neutral-500">Loading…</div>}
            {stores?.map((s) => <StoreTile key={s.seller_id} s={s} />)}
            {stores?.length === 0 && <div className="text-sm text-neutral-500">No stores yet.</div>}
          </div>
        </section>
      </div>
      <StagePostViewer postId={params.get("post")} onClose={() => { params.delete("post"); setParams(params); }} />
    </div>
  );
}

function StoreTile({ s }) {
  return (
    <Link to={`/store/${s.seller_id}`} data-testid={`stages-store-${s.seller_id}`} className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#111] p-4 hover:border-white/30 transition-colors">
      {s.cover_url && <img src={s.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity" />}
      <div className="relative flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl bg-[#1c1c1e] overflow-hidden flex items-center justify-center font-black shrink-0">{s.avatar_url ? <img src={s.avatar_url} alt="" className="w-full h-full object-cover" /> : (s.name || "S")[0]}</div>
        <div className="min-w-0 flex-1">
          <div className="font-bold truncate flex items-center gap-2">{s.name}{s.live && <span className="rounded-full bg-[#FF3B30] px-1.5 py-0.5 text-[9px] font-black uppercase">Live</span>}</div>
          <div className="text-xs text-neutral-400 truncate">{s.tagline || `${s.products} products on VISMARKS`}</div>
          <div className="mt-1 flex gap-3 text-[11px] text-neutral-500"><span className="inline-flex items-center gap-1"><Clapperboard className="h-3 w-3" />{s.posts}</span><span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{s.audience}</span><span className="inline-flex items-center gap-1"><Package className="h-3 w-3" />{s.products}</span></div>
        </div>
      </div>
    </Link>
  );
}
